/** Stub responses until a real companion backend is wired up. */

import { ARCHIV } from "./appRoutes";
import { LOGIN_SCHULE_FROM_CHAT, guestLoginHref } from "./guestAcquisition";
import {
  CHAT_GUEST_KITA_BRIDGE_TEXT,
  CHAT_GUEST_KITA_CTA_LABEL,
  CHAT_GUEST_KITA_CTA_TEXT,
  CHAT_GUEST_KITA_DEMO_TEXT,
  CHAT_MISS_TEXT,
  CHAT_WELCOME_GUEST_TEXT,
} from "./albisVoice";
import {
  ERSTE_HAUSHALTE_CTA_LABEL,
  ersteHaushalteDemoText,
  ersteHaushalteLoginHref,
  ersteHaushalteWelcomeText,
  isErsteHaushalteScenario,
  parseErsteHaushalteAnswer,
  parseSchoolDemoVariant,
} from "./ersteHaushalte";
import { MAIL_SETUP_ASK_ID } from "./schoolSetupChat";

export const WELCOME_MESSAGE_ID = "welcome";
export const GUEST_KITA_DEMO_ID = "guest-kita-demo";

/**
 * Wedge + local calendar Schnellfragen for guests / pre-extraction.
 * Copy: docs/albis-voice.md (first person OK, gender-neutral, Family Manager in English).
 */

/** Quick chip — guests get a labeled Beispiel; signed-in fetch the live schedule. */
export const SCHEDULE_RECALL_PROMPT_ID = "schedule-recall";

/** Ferien/Feiertage chip — answered via regional calendar API (guests + signed-in). */
export const HOLIDAY_RECALL_PROMPT_ID = "holiday-recall";

/** Signed-in, no mail yet — opens forwarding setup in chat. */
export const SCHOOL_SETUP_PROMPT_ID = "school-setup";

/** Signed-in, KiTa days unknown — opens Betreuungstage. */
export const KIDS_DAYS_PROMPT_ID = "kids-days";

/** Signed-in, days known — recap / edit children. */
export const KIDS_MEMORY_PROMPT_ID = "kids-memory";

/** @type {Record<string, { guest: object, signedIn: object }>} */
export const CHAT_PROMPTS = {
  [HOLIDAY_RECALL_PROMPT_ID]: {
    guest: {
      label: "🗓️ Wann sind die Ferien?",
      message: "Wann sind die nächsten Schulferien?",
      reply: {
        text: "Schulferien und Feiertage für Stadt Zürich kenne ich — frag mich einfach.",
      },
    },
    signedIn: {
      label: "🗓️ Wann sind die Ferien?",
      message: "Wann sind die nächsten Schulferien?",
      reply: {
        text: "Schulferien und Feiertage für Stadt Zürich kenne ich — frag mich einfach.",
      },
    },
  },
};

/** Guest «Was steht an?» — Beispiel card, then signup. */
export function getGuestScheduleRecallReply({
  demoVariant,
  utmScenario = null,
  jobAnswer = null,
} = {}) {
  const variant = parseSchoolDemoVariant(demoVariant);
  const fromErsteHaushalte = isErsteHaushalteScenario(utmScenario);
  const loginPath = fromErsteHaushalte
    ? ersteHaushalteLoginHref({ answer: jobAnswer || parseErsteHaushalteAnswer(null) })
    : guestLoginHref(utmScenario, { back: "/" });
  return {
    text: `${CHAT_GUEST_KITA_DEMO_TEXT}\n\n${CHAT_GUEST_KITA_CTA_TEXT}`,
    content: "school-demo",
    demoVariant: variant,
    action: { type: "navigate", path: loginPath },
    actionLabel: fromErsteHaushalte
      ? ERSTE_HAUSHALTE_CTA_LABEL
      : CHAT_GUEST_KITA_CTA_LABEL,
  };
}

/**
 * Auto-shown guest thread: miss → compact demo + bridge + CTA.
 * @param {{ demoVariant?: string, ersteHaushalte?: boolean, jobAnswer?: string }} [opts]
 */
export function buildGuestWelcomeMessages({
  demoVariant,
  ersteHaushalte = false,
  jobAnswer = "deadlines",
} = {}) {
  const variant = parseSchoolDemoVariant(demoVariant);
  const loginPath = ersteHaushalte
    ? ersteHaushalteLoginHref({ answer: jobAnswer })
    : LOGIN_SCHULE_FROM_CHAT;
  const firstText = ersteHaushalte
    ? ersteHaushalteWelcomeText({ answer: jobAnswer })
    : CHAT_WELCOME_GUEST_TEXT;
  const demoText = ersteHaushalte
    ? ersteHaushalteDemoText()
    : CHAT_GUEST_KITA_BRIDGE_TEXT;
  return [
    {
      id: WELCOME_MESSAGE_ID,
      role: "assistant",
      text: firstText,
    },
    {
      id: GUEST_KITA_DEMO_ID,
      role: "assistant",
      text: demoText,
      content: "school-demo",
      demoVariant: variant,
      action: { type: "navigate", path: loginPath },
      actionLabel: ersteHaushalte
        ? ERSTE_HAUSHALTE_CTA_LABEL
        : CHAT_GUEST_KITA_CTA_LABEL,
    },
  ];
}

/**
 * Guest: miss + demo at the top. Signed-in: no Hallo splash (briefing / setup ask instead).
 * @param {object[]} prev
 * @param {{ signedIn?: boolean, demoVariant?: string, ersteHaushalte?: boolean, jobAnswer?: string }} [opts]
 */
export function syncAuthChatWelcome(
  prev,
  { signedIn = false, demoVariant, ersteHaushalte = false, jobAnswer } = {},
) {
  const list = Array.isArray(prev) ? prev : [];
  if (signedIn) {
    return list.filter(
      (m) => m.id !== WELCOME_MESSAGE_ID && m.id !== GUEST_KITA_DEMO_ID,
    );
  }
  const rest = list.filter(
    (m) =>
      m.id !== WELCOME_MESSAGE_ID &&
      m.id !== GUEST_KITA_DEMO_ID &&
      m.id !== MAIL_SETUP_ASK_ID,
  );
  return [
    ...buildGuestWelcomeMessages({ demoVariant, ersteHaushalte, jobAnswer }),
    ...rest,
  ];
}

/**
 * Composer chips: asks parents can tap without guessing wording.
 * Signed-in: schedule + Kinder + Ferien. Setup stays a thread beat, not a chip.
 *
 * @param {{ signedIn?: boolean }} [ctx]
 */
export function getChatQuickPrompts(ctx = {}) {
  const prompts = [getScheduleRecallQuickPrompt()];
  if (ctx.signedIn) prompts.push(getKidsMemoryQuickPrompt());
  prompts.push(getHolidayRecallQuickPrompt());
  return prompts;
}

/** @param {string} promptId @param {{ signedIn?: boolean, demoVariant?: string, utmScenario?: string | null, jobAnswer?: string }} [opts] */
export function getPromptReply(
  promptId,
  { signedIn = false, demoVariant, utmScenario = null, jobAnswer = null } = {},
) {
  if (promptId === SCHEDULE_RECALL_PROMPT_ID) {
    return signedIn
      ? getScheduleRecallPromptReply()
      : getGuestScheduleRecallReply({ demoVariant, utmScenario, jobAnswer });
  }
  const variants = CHAT_PROMPTS[promptId];
  if (!variants) return null;
  return (signedIn ? variants.signedIn : variants.guest).reply;
}

/** @param {string} text */
export function isHolidayQuestion(text) {
  const t = String(text || "").toLowerCase();
  return (
    /schulferien|ferien|feiertag|feiertage|frei\s*(?:hat|haben|tag)|schule\s*zu|kita\s*zu|öffentlicher\s*feiertag/.test(
      t,
    ) ||
    (/wann|bis wann|ab wann|nächste|naechste|kommende/.test(t) &&
      /ferien|feiertag/.test(t)) ||
    (/ist\s/.test(t) &&
      /montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|morgen|übermorgen|uebermorgen|\d{1,2}\.\d{1,2}/.test(
        t,
      ) &&
      /feiertag|frei|ferien/.test(t))
  );
}

/** @param {string} text */
export function isSchoolDeadlineQuestion(text) {
  const t = String(text || "").toLowerCase();
  if (isHolidayQuestion(t)) return false;
  return /kita|kindergarten|schule|frist|deadline/.test(t);
}

/** KiTa/schedule recall in chat — same horizon as the agenda card. */
export function isScheduleRecallQuestion(text) {
  const t = String(text || "").toLowerCase();
  if (isHolidayQuestion(t)) return false;
  if (isSchoolDeadlineQuestion(t)) return true;
  if (
    /steht\s+(?:\S+\s+)?an|ansteht|was ansteht|anstehend|termine und fristen|termine & fristen/.test(
      t,
    )
  ) {
    return true;
  }
  if (
    /\b(heute|morgen|diese woche)\b/.test(t) &&
    /was |termin|frist|aufgabe|kita|schule/.test(t)
  ) {
    return true;
  }
  if (/mitbringen|elternabend|waldtag|flohmarkt/.test(t)) return true;
  return false;
}

export function getScheduleRecallQuickPrompt() {
  return {
    id: SCHEDULE_RECALL_PROMPT_ID,
    label: "📅 Was steht an?",
    message: "Was steht an?",
  };
}

export function getHolidayRecallQuickPrompt() {
  const variants = CHAT_PROMPTS[HOLIDAY_RECALL_PROMPT_ID];
  return {
    id: HOLIDAY_RECALL_PROMPT_ID,
    label: variants.guest.label,
    message: variants.guest.message,
  };
}

export function getSchoolSetupQuickPrompt() {
  return {
    id: SCHOOL_SETUP_PROMPT_ID,
    label: "✉️ Weiterleitung",
    message: "Weiterleitung einrichten",
    kind: "school-setup",
  };
}

export function getKidsDaysQuickPrompt() {
  return {
    id: KIDS_DAYS_PROMPT_ID,
    label: "KiTa-Tage",
    message: "KiTa-Tage ändern",
    kind: "kids-days",
  };
}

export function getKidsMemoryQuickPrompt() {
  return {
    id: KIDS_MEMORY_PROMPT_ID,
    label: "Kinder",
    message: "Welche Kinder habe ich?",
    kind: "kids-memory",
  };
}

/** Hide composer chips while Albis is asking a guided next step. */
export function messageBlocksComposerChips(msg) {
  if (!msg || msg.role !== "assistant") return false;
  if (Array.isArray(msg.options) && msg.options.length > 0) return true;
  if (msg.content === "capture-confirm-picker" && !msg.capturePickerDone) {
    return true;
  }
  if (msg.content === "kita-remind-picker" && !msg.remindPickerDone) {
    return true;
  }
  if (msg.content === "age-capture" && !msg.ageCaptureDone) {
    return true;
  }
  return false;
}

/**
 * @param {{
 *   promptCount?: number,
 *   feedbackFlowActive?: boolean,
 *   isThinking?: boolean,
 *   guidedFlowActive?: boolean,
 *   lastMessage?: object | null,
 * }} [opts]
 */
export function shouldShowComposerChips(opts = {}) {
  const promptCount = Number(opts.promptCount) || 0;
  if (promptCount < 1) return false;
  if (opts.feedbackFlowActive) return false;
  if (opts.isThinking) return false;
  if (opts.guidedFlowActive) return false;
  if (messageBlocksComposerChips(opts.lastMessage)) return false;
  return true;
}

/** @returns {{ needsScheduleData: true }} */
export function getScheduleRecallPromptReply() {
  return { needsScheduleData: true };
}

/** @deprecated Use buildScheduleRecallReply from chatScheduleRecall.js */
export { buildScheduleRecallReply as buildSchoolDeadlineReply } from "./chatScheduleRecall.js";

export function getStubReply(_userMessage, context = {}) {
  const { isAdmin = false } = context;
  const text = String(_userMessage || "").trim().toLowerCase();

  if (isAdmin && /archiv|entdeck|plan/.test(text)) {
    return {
      text: "Plan und Entdecken liegen im Archiv — Inhalte werden nicht mehr gepflegt.",
      action: { type: "navigate", path: ARCHIV },
      actionLabel: "Archiv öffnen",
    };
  }

  return {
    text: CHAT_MISS_TEXT,
    unmatched: true,
    action: null,
    actionLabel: null,
  };
}
